import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  getAllEntries,
  putEntry,
  deleteEntry,
  getSetting,
  setSetting,
  getAllDrafts,
  deleteDraft,
} from "../utilities/visitQueueDB";
import { createVisitSheet } from "../services/visitsheets.service";
import { UsuarioContext } from "./userContext";

const RETRY_INTERVAL_MS = 3 * 60 * 1000; // 3 minutos

const STALE_PROCESSING_MS = 2.5 * 60 * 1000; // 2 minutos y medio
const PAUSE_ALL_KEY = "pausedAll";
const SENT_KEY = "visitQueueSent";
// Antigüedad máxima de un borrador no enviado ni encolado antes de borrarse.
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // ~1 mes

const leerEnviadas = () => {
  try {
    const raw = sessionStorage.getItem(SENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const guardarEnviadas = (lista) => {
  try {
    sessionStorage.setItem(SENT_KEY, JSON.stringify(lista));
  } catch (e) {
    /* noop */
  }
};

// Nº de intentos con error de SERVIDOR (5xx) tras el que se marca como "error".
export const MAX_SERVER_ATTEMPTS = 10;
// Nº de intentos con error de CLIENTE (4xx) tras el que se marca como "error".
export const MAX_CLIENT_ATTEMPTS = 2;

// Mensajes por defecto para errores de cliente (si el backend no envía uno).
const MENSAJES_CLIENTE = {
  400: "Datos inválidos. Revisa el formulario.",
  401: "No autorizado.",
  403: "No tienes permisos para realizar esta acción.",
  404: "No se encuentra el recurso (404).",
  409: "La hoja de visita ya existe.",
  413: "El contenido es demasiado grande.",
  422: "Datos no válidos.",
};

export const VisitQueueContext = createContext(null);

export const useVisitQueue = () => {
  const ctx = useContext(VisitQueueContext);
  if (!ctx) {
    // Permite usar el hook sin provider (devuelve estado vacío) para no romper
    // componentes que se rendericen fuera del árbol del provider.
    return {
      entries: [],
      pendingCount: 0,
      errorCount: 0,
      totalCount: 0,
      foreignCount: 0,
      sentEntries: [],
      sentCount: 0,
      pausedAll: false,
      retryIntervalMs: 0,
      enqueue: async () => {},
      removeEntry: async () => {},
      processEntry: async () => {},
      processAll: async () => {},
      setPausedEntry: async () => {},
      togglePauseAll: () => {},
      addSent: () => {},
      removeSent: () => {},
      clearSent: () => {},
    };
  }
  return ctx;
};

// Clasifica la respuesta de createVisitSheet. Devuelve la respuesta de axios o
// undefined ante error de red. OJO: el backend a veces manda el código en el
// CUERPO (p.ej. res.send({ status: 409 })) con HTTP 200, por lo que se mira
// tanto res.status como res.data.status.
//
// tipo: "exito" | "red" (sin conexión) | "cliente" (4xx) | "servidor" (5xx)
export const clasificarRespuestaVisita = (res) => {
  const httpStatus = res?.status;
  const bodyStatus = res?.data?.status;
  const code = bodyStatus || httpStatus || null;

  // Sin respuesta del servidor → problema de conexión.
  if (!res || !code) {
    return {
      tipo: "red",
      code: null,
      error: { kind: "network", status: null, message: "Sin conexión / error de red" },
    };
  }

  // Este backend SOLO confirma la creación de la hoja de visita con un 201
  // (VISITSHEET_CREATED). El "ya existe" llega como 200 pero con status 409 en el
  // cuerpo, que ya se ha capturado arriba en `code`.
  if (code === 201) {
    return { tipo: "exito", code };
  }
  // OJO: un "200 a secas" (proxy, portal cautivo, gateway, página HTML intermedia…)
  // NO es una creación válida. Tratarlo como éxito BORRARÍA la visita de la cola sin
  // haberse guardado en el servidor. Se trata como error de servidor recuperable.
  if (code === 200) {
    return {
      tipo: "servidor",
      code,
      error: {
        kind: "server",
        status: 200,
        message:
          "Respuesta inesperada del servidor (200 sin confirmación de guardado). Se reintentará.",
      },
    };
  }

  // Mensaje: prioriza el que envía el backend.
  let message = null;
  const data = res.data;
  if (data) {
    if (Array.isArray(data.error)) message = data.error.join(", ");
    else if (typeof data.error === "string") message = data.error;
    else if (typeof data.message === "string") message = data.message;
  }
  if (!message) message = MENSAJES_CLIENTE[code] || `Error ${code}`;

  if (code >= 400 && code < 500) {
    return { tipo: "cliente", code, error: { kind: "client", status: code, message } };
  }
  return { tipo: "servidor", code, error: { kind: "server", status: code, message } };
};

export const VisitQueueProvider = ({ children }) => {
  const { t } = useTranslation(["properties", "common"]);
  const user = useContext(UsuarioContext);
  const currentUserId = user?.id ?? null;
  const [entries, setEntries] = useState([]);
  const [sentEntries, setSentEntries] = useState(() => leerEnviadas());
  const [pausedAll, setPausedAll] = useState(false);
  const pausedAllRef = useRef(false);
  const currentUserIdRef = useRef(currentUserId);
  const processingRef = useRef(false);
  const entriesRef = useRef([]);
  const foreignToastUserRef = useRef(null);

  useEffect(() => {
    pausedAllRef.current = pausedAll;
  }, [pausedAll]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const refresh = useCallback(async () => {
    try {
      const all = await getAllEntries();
      const ordenadas = (all || []).sort(
        (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
      );
      entriesRef.current = ordenadas;
      setEntries(ordenadas);
      return ordenadas;
    } catch (err) {
      console.error("Error leyendo la cola de visitas:", err);
      return [];
    }
  }, []);

  // Rescata entradas atascadas en "processing".
  const rescatarBloqueadas = useCallback(
    async (lista, { soloAntiguas = false } = {}) => {
      const now = Date.now();
      const bloqueadas = (lista || []).filter(
        (e) =>
          e.status === "processing" &&
          (!soloAntiguas || now - (e.updatedAt || 0) >= STALE_PROCESSING_MS)
      );
      if (!bloqueadas.length) return false;
      for (const e of bloqueadas) {
        // eslint-disable-next-line no-await-in-loop
        await putEntry({ ...e, status: "pending", nextRetryAt: now, updatedAt: now });
      }
      await refresh();
      return true;
    },
    [refresh]
  );

  useEffect(() => {
    // Al cargar (no hay ninguna petición en vuelo) se rescatan todas las que
    // hubieran quedado "enviando" de una sesión anterior interrumpida.
    refresh().then((all) => rescatarBloqueadas(all));
    getSetting(PAUSE_ALL_KEY)
      .then((value) => {
        const v = value === true;
        pausedAllRef.current = v;
        setPausedAll(v);
      })
      .catch(() => {});
    // Purga de borradores caducados (> ~1 mes) que NO estén en la cola.
    (async () => {
      try {
        const [drafts, pending] = await Promise.all([
          getAllDrafts(),
          getAllEntries(),
        ]);
        const idsEnCola = new Set((pending || []).map((e) => e.id));
        const ahora = Date.now();
        for (const d of drafts || []) {
          if (idsEnCola.has(d.id)) continue; // pasado a la cola → exento
          if (ahora - (d.createdAt || 0) > DRAFT_TTL_MS) {
            // eslint-disable-next-line no-await-in-loop
            await deleteDraft(d.id);
          }
        }
      } catch (e) {
        console.error("Error purgando borradores caducados:", e);
      }
    })();
  }, [refresh, rescatarBloqueadas]);

  const enqueue = useCallback(
    async (entry) => {
      const now = Date.now();
      const existente = entriesRef.current.find((e) => e.id === entry.id);
      const nextRetryAt =
        existente?.nextRetryAt && existente.nextRetryAt > now
          ? existente.nextRetryAt
          : now + RETRY_INTERVAL_MS;
      const registro = {
        status: "pending",
        attempts: 0,
        serverAttempts: 0,
        clientAttempts: 0,
        paused: false,
        lastError: null,
        createdAt: existente?.createdAt || now,
        nextRetryAt,
        ...entry,
        // El provider es la fuente autoritativa del propietario de la entrada.
        userId: currentUserId ?? entry.userId ?? null,
        userName:
          (user ? `${user.nombre || ""} ${user.apellidos || ""}`.trim() : entry.userName) || null,
        updatedAt: now,
      };
      await putEntry(registro);
      await refresh();
      return registro;
    },
    [refresh, currentUserId, user]
  );

  const removeEntry = useCallback(
    async (id) => {
      await deleteEntry(id);
      try {
        await deleteDraft(id);
      } catch (e) {
        /* noop */
      }
      await refresh();
    },
    [refresh]
  );

  const addSent = useCallback((record) => {
    setSentEntries((prev) => {
      const sinDuplicado = prev.filter((r) => r.id !== record.id);
      const next = [{ ...record }, ...sinDuplicado].slice(0, 100);
      guardarEnviadas(next);
      return next;
    });
  }, []);

  const removeSent = useCallback((id) => {
    setSentEntries((prev) => {
      const next = prev.filter((r) => r.id !== id);
      guardarEnviadas(next);
      return next;
    });
  }, []);

  const clearSent = useCallback(() => {
    setSentEntries([]);
    guardarEnviadas([]);
  }, []);

  // Pausa/reanuda una entrada concreta (no se reintentará automáticamente).
  const setPausedEntry = useCallback(
    async (id, paused) => {
      const entry = entriesRef.current.find((e) => e.id === id);
      if (!entry) return;
      const now = Date.now();
      const cambios = { ...entry, paused, updatedAt: now };

      if (!paused) cambios.nextRetryAt = now + RETRY_INTERVAL_MS;
      await putEntry(cambios);
      await refresh();
    },
    [refresh]
  );

  // Se persiste en IndexedDB para que sobreviva al logout y al cambio de usuario.
  const togglePauseAll = useCallback(() => {
    setPausedAll((prev) => {
      const next = !prev;
      pausedAllRef.current = next;
      setSetting(PAUSE_ALL_KEY, next).catch(() => {});
      return next;
    });
  }, []);

  // Procesa una entrada concreta. force=true permite reintentar entradas en "error".
  const processEntry = useCallback(
    async (id, { force = false, silent = false } = {}) => {
      const entry = entriesRef.current.find((e) => e.id === id);
      if (!entry) return;
      if (entry.status === "processing") return;
      if (entry.status === "error" && !force) return;

      // Marca como "processing".
      const enProceso = { ...entry, status: "processing", updatedAt: Date.now() };
      await putEntry(enProceso);
      await refresh();

      let res;
      try {
        res = await createVisitSheet(
          entry.datos,
          entry.images || [],
          entry.mainImage,
          entry.params?.nif
        );
      } catch (err) {
        res = undefined; // tratado como error de red
      }

      const resultado = clasificarRespuestaVisita(res);
      const now = Date.now();

      if (resultado.tipo === "exito") {
        await deleteEntry(id);
        try {
          await deleteDraft(id);
        } catch (e) {
          /* nope */
        }

        addSent({
          id: entry.id,
          comunidad: entry.comunidad,
          tecnicoPRL: entry.tecnicoPRL,
          userId: entry.userId,
          userName: entry.userName,
          sentAt: now,
        });
        await refresh();
        toast.success(
          t("visitQueue.toast.sent", {
            defaultValue: "Hoja de visita enviada correctamente desde la cola",
            comunidad: entry.comunidad || "",
          })
        );
        return;
      }

      const actualizado = {
        ...entry,
        attempts: (entry.attempts || 0) + 1,
        lastError: resultado.error,
        lastAttemptAt: now,
        nextRetryAt: now + RETRY_INTERVAL_MS,
        updatedAt: now,
      };

      if (resultado.tipo === "red") {
        // Error de conexión: reintentos ilimitados, vuelve a "pending".
        actualizado.status = "pending";
      } else if (resultado.tipo === "cliente") {
        // Error de cliente (4xx): pocos intentos y se marca "error" (no recuperable).
        actualizado.clientAttempts = (entry.clientAttempts || 0) + 1;
        actualizado.status =
          actualizado.clientAttempts >= MAX_CLIENT_ATTEMPTS ? "error" : "pending";
      } else {
        // Error de servidor (5xx): cuenta intentos y marca "error" tras el máximo.
        actualizado.serverAttempts = (entry.serverAttempts || 0) + 1;
        actualizado.status =
          actualizado.serverAttempts >= MAX_SERVER_ATTEMPTS ? "error" : "pending";
      }

      await putEntry(actualizado);
      await refresh();

      if (!silent && actualizado.status === "error") {
        if (resultado.tipo === "cliente") {
          // Fallo de cliente: mostramos el motivo concreto (ya existe, no se encuentra...).
          toast.error(
            t("visitQueue.toast.clientError", {
              defaultValue: "No se puede subir: {{motivo}}",
              motivo: resultado.error.message,
            })
          );
        } else {
          toast.error(
            t("visitQueue.toast.markedError", {
              defaultValue:
                "Una hoja de visita no se pudo enviar tras varios intentos (error del servidor)",
            })
          );
        }
      }
    },
    [refresh, t, addSent]
  );

  const processAll = useCallback(
    async ({ includeErrors = false, auto = false, onlyDue = false } = {}) => {
      if (processingRef.current) return;
      if (auto && pausedAllRef.current) return;
      const uid = currentUserIdRef.current;
      if (!uid) return; // la cola sólo funciona con una sesión activa
      processingRef.current = true;
      try {
        const all = await refresh();
        const now = Date.now();
        for (const entry of all) {
          // Sólo procesamos entradas del usuario actual (o sin propietario).
          if (entry.userId && entry.userId !== uid) continue;
          if (entry.paused) continue; // pausada individualmente
          if (entry.status === "error" && !includeErrors) continue;
          if (onlyDue && entry.status !== "error" && (entry.nextRetryAt || 0) > now)
            continue;
          // eslint-disable-next-line no-await-in-loop
          await processEntry(entry.id, { force: includeErrors, silent: true });
        }
      } finally {
        processingRef.current = false;
      }
    },
    [refresh, processEntry]
  );

  // Temporizador de reintento automático. Un "latido" cada segundo mira
  // (en memoria, sin leer IndexedDB) si a alguna entrada ya le toca por
  // su propio contador; sólo entonces lanza el procesado.
  // Como el contador vive en cada entrada, persiste tras un F5.
  useEffect(() => {
    const algunaToca = () => {
      if (pausedAllRef.current) return false;
      const uid = currentUserIdRef.current;
      if (!uid) return false;
      const now = Date.now();
      return entriesRef.current.some(
        (e) =>
          (!e.userId || e.userId === uid) &&
          !e.paused &&
          e.status !== "error" &&
          e.status !== "processing" &&
          (e.nextRetryAt || 0) <= now
      );
    };

    const intervalId = setInterval(() => {

      rescatarBloqueadas(entriesRef.current, { soloAntiguas: true });
      if (navigator.onLine === false) return;
      if (algunaToca()) processAll({ auto: true, onlyDue: true });
    }, 1000);

    // Al recuperar conexión, se reintentan de inmediato todas las pendientes.
    const onOnline = () => processAll({ auto: true });
    window.addEventListener("online", onOnline);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("online", onOnline);
    };
  }, [processAll, rescatarBloqueadas]);

  // Entradas del usuario actual (o legadas sin propietario) y ajenas.
  const misEntries = useMemo(
    () => entries.filter((e) => !e.userId || e.userId === currentUserId),
    [entries, currentUserId]
  );
  const foreignCount = useMemo(
    () => entries.filter((e) => e.userId && e.userId !== currentUserId).length,
    [entries, currentUserId]
  );

  // Aviso de que hay visitas en cola de otro usuario.
  useEffect(() => {
    if (!currentUserId) return;
    if (foreignToastUserRef.current === currentUserId) return;
    if (foreignCount > 0) {
      foreignToastUserRef.current = currentUserId;
      toast.info(
        t("visitQueue.toast.otherUser", {
          count: foreignCount,
          defaultValue:
            "Hay {{count}} hoja(s) de visita en cola de otro usuario. Inicia sesión con ese usuario para enviarlas.",
        })
      );
    }
  }, [currentUserId, foreignCount, t]);

  const pendingCount = useMemo(
    () => misEntries.filter((e) => e.status !== "error").length,
    [misEntries]
  );
  const errorCount = useMemo(
    () => misEntries.filter((e) => e.status === "error").length,
    [misEntries]
  );

  // Enviadas del usuario actual (o sin propietario).
  const misEnviadas = useMemo(
    () => sentEntries.filter((e) => !e.userId || e.userId === currentUserId),
    [sentEntries, currentUserId]
  );

  const value = useMemo(
    () => ({
      entries: misEntries,
      pendingCount,
      errorCount,
      totalCount: misEntries.length,
      foreignCount,
      sentEntries: misEnviadas,
      sentCount: misEnviadas.length,
      pausedAll,
      retryIntervalMs: RETRY_INTERVAL_MS,
      enqueue,
      removeEntry,
      processEntry,
      processAll,
      setPausedEntry,
      togglePauseAll,
      addSent,
      removeSent,
      clearSent,
    }),
    [
      misEntries,
      pendingCount,
      errorCount,
      foreignCount,
      misEnviadas,
      pausedAll,
      enqueue,
      removeEntry,
      processEntry,
      processAll,
      setPausedEntry,
      togglePauseAll,
      addSent,
      removeSent,
      clearSent,
    ]
  );

  return (
    <VisitQueueContext.Provider value={value}>
      {children}
    </VisitQueueContext.Provider>
  );
};

export default VisitQueueProvider;
